import { Link } from 'react-router-dom';
import { VideoItem } from '@/mocks/videos';

interface VideoGridProps {
  title: string;
  subtitle?: string;
  videos: VideoItem[];
  linkTo?: string;
  badge?: string;
}

function formatViews(views: number): string {
  if (views >= 100000000) return `${(views / 100000000).toFixed(1)}亿`;
  if (views >= 10000) return `${(views / 10000).toFixed(0)}万`;
  return views.toString();
}

export default function VideoGrid({ title, subtitle, videos, linkTo, badge }: VideoGridProps) {
  return (
    <section className="py-12 bg-background-50">
      <div className="container-main">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <h2 className="text-2xl font-bold text-foreground-900">{title}</h2>
              {badge && (
                <span className="px-2.5 py-0.5 rounded-full bg-primary-100 text-primary-700 text-xs font-medium">
                  {badge}
                </span>
              )}
            </div>
            {subtitle && (
              <p className="text-sm text-foreground-500">{subtitle}</p>
            )}
          </div>
          {linkTo && (
            <Link
              to={linkTo}
              className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700 font-medium whitespace-nowrap"
            >
              查看更多
              <i className="ri-arrow-right-line"></i>
            </Link>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {videos.map((video) => (
            <Link
              key={video.id}
              to={`/category/${video.category}`}
              className="group bg-background-50 rounded-xl overflow-hidden border border-background-200/70 hover:border-background-300 transition-all duration-300"
            >
              <div className="relative aspect-video overflow-hidden bg-background-200">
                <img
                  src={video.thumbnail}
                  alt={video.title}
                  title={`${video.title} - 茄子视频在线观看`}
                  className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  loading="lazy"
                />
                <div className="absolute bottom-2 right-2 px-2 py-0.5 bg-black/70 text-white text-xs rounded">
                  {video.duration}
                </div>
                <div className="absolute top-2 left-2 px-2 py-0.5 bg-primary-500 text-white text-xs rounded">
                  {video.resolution}
                </div>
                <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
                    <i className="ri-play-fill text-primary-600 text-xl ml-0.5"></i>
                  </div>
                </div>
              </div>
              <div className="p-4">
                <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors">
                  {video.title}
                </h3>
                <div className="flex items-center justify-between text-xs text-foreground-500">
                  <div className="flex items-center gap-1">
                    <i className="ri-eye-line"></i>
                    <span>{formatViews(video.views)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <i className="ri-star-fill text-accent-500 text-xs"></i>
                    <span>{video.rating}</span>
                  </div>
                </div>
                {video.tags && video.tags.length > 0 && (
                  <div className="flex items-center gap-1.5 mt-2 flex-wrap">
                    {video.tags.slice(0, 2).map((tag) => (
                      <span
                        key={tag}
                        className="px-2 py-0.5 bg-secondary-50 text-secondary-700 text-xs rounded"
                      >
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}